import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Inbox, Users, FileText, Briefcase, AlertTriangle, Star, ShieldCheck, Eye } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatCard, PageHeader, Loader, Empty } from "@/components/Common";
import { StatusPill, ReadinessBadge, Stars, DemoBadge } from "@/components/Badges";
import PassportCard from "@/components/PassportCard";
import { api, errMsg, fmtDate, label } from "@/lib/api";

const useDash = () => useQuery({ queryKey: ["provider-dash"], queryFn: () => api.get("/provider/me/dashboard").then((r) => r.data), retry: false });

export function AcceptingToggle() {
  const { data } = useDash();
  const qc = useQueryClient();
  if (!data) return null;
  const on = data.org.accepting_new_clients;
  const toggle = async (v) => {
    try { await api.patch("/provider/me/accepting", { accepting: v }); qc.invalidateQueries({ queryKey: ["provider-dash"] }); toast.success(v ? "You're accepting new clients" : "Marked as not accepting new clients"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <label className={`flex items-center gap-2.5 rounded-full border pl-3 pr-1.5 py-1 ${on ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"}`} data-testid="accepting-clients-control">
      <span className={`text-xs font-bold ${on ? "text-emerald-700" : "text-slate-600"}`}>{on ? "Accepting New Clients" : "Not Accepting"}</span>
      <Switch checked={on} onCheckedChange={toggle} className="data-[state=checked]:bg-emerald-500" data-testid="accepting-clients-toggle" />
    </label>
  );
}

export function ProviderOverview() {
  const { data, isLoading, error } = useDash();
  if (isLoading) return <Loader />;
  if (error) return <Empty title="Set up your organization" body="Complete your provider profile to start receiving care requests." action={<Button asChild className="rounded-full bg-[#003B2D]"><Link to="/provider/profile">Complete onboarding</Link></Button>} />;
  const m = data.metrics;
  return (
    <div data-testid="provider-overview">
      <PageHeader title={data.org.name} subtitle="Your Zunvel organization at a glance." actions={<Button asChild variant="outline" className="rounded-full"><Link to={`/providers/${data.org.slug}`}>View storefront</Link></Button>} />
      {data.org.status !== "approved" && <div className="mb-6 rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900" data-testid="org-status-banner">Your listing status is <b>{label(data.org.status)}</b>. Zunvel reviews new providers before they appear in search.</div>}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard icon={Inbox} label="New Care Requests" value={m.new_care_requests} testid="metric-new-care-requests" />
        <StatCard icon={Users} label="Worker Matches" value={m.worker_matches} testid="metric-worker-matches" tone="navy" />
        <StatCard icon={FileText} label="Applications" value={m.applications} testid="metric-applications" />
        <StatCard icon={Briefcase} label="Open Jobs" value={m.open_jobs} testid="metric-open-jobs" tone="navy" />
        <StatCard icon={AlertTriangle} label="Credentials Expiring" value={m.credentials_expiring} hint="Applicants, next 30 days" testid="metric-credentials-expiring" tone="amber" />
        <StatCard icon={Star} label="Provider Rating" value={m.rating_avg ? m.rating_avg.toFixed(1) : "New"} hint={`${m.rating_count} reviews`} testid="metric-provider-rating" />
      </div>
      <div className="cz-card p-6 mt-6">
        <div className="font-display font-bold mb-3">Recent care requests</div>
        {data.recent_requests.length === 0 ? <p className="text-sm text-slate-500">No requests yet.</p> : data.recent_requests.map((r) => (
          <div key={r.id} className="py-2.5 border-b last:border-0 flex justify-between items-center text-sm"><span>{r.contact_name} · {fmtDate(r.created_at)} {r.is_demo && <DemoBadge className="ml-1" />}</span><StatusPill status={r.status} /></div>
        ))}
      </div>
    </div>
  );
}

export function ProviderRequests({ leadsOnly }) {
  const { data, isLoading, refetch } = useQuery({ queryKey: ["provider-requests"], queryFn: () => api.get("/provider/me/care-requests").then((r) => r.data) });
  const update = async (id, status) => { try { await api.patch(`/provider/me/care-requests/${id}`, { status }); refetch(); toast.success("Updated"); } catch (e) { toast.error(errMsg(e)); } };
  if (isLoading) return <Loader />;
  const rows = leadsOnly ? data.filter((r) => ["new", "viewed"].includes(r.status)) : data;
  return (
    <div data-testid={leadsOnly ? "provider-leads" : "provider-care-requests"}>
      <PageHeader title={leadsOnly ? "Client Leads" : "Care Requests"} subtitle={leadsOnly ? "New families who reached out and haven't been contacted yet." : "All care requests sent to your organization."} />
      {rows.length === 0 ? <Empty title="Nothing here yet" body="Keep Accepting New Clients ON to appear first in family searches." /> : (
        <div className="grid gap-4">{rows.map((r) => (
          <div key={r.id} className="cz-card p-5 flex flex-col md:flex-row gap-4 md:items-center justify-between" data-testid={`care-request-${r.id}`}>
            <div className="space-y-1">
              <div className="flex items-center gap-2"><span className="font-semibold text-slate-900">{r.contact_name}</span>{r.is_demo && <DemoBadge />}<StatusPill status={r.status} /></div>
              <div className="text-sm text-slate-600">{r.care_type} · {r.county} County · {r.needs?.schedule} · {r.needs?.urgency}</div>
              <div className="text-sm text-slate-500">{r.contact_email}{r.contact_phone && ` · ${r.contact_phone}`}</div>
              {r.message && <p className="text-sm text-slate-700 mt-1 max-w-2xl">"{r.message}"</p>}
            </div>
            <Select value={r.status} onValueChange={(v) => update(r.id, v)}>
              <SelectTrigger className="w-44" data-testid={`care-request-status-${r.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>{["new", "viewed", "contacted", "accepted", "declined", "closed"].map((s) => <SelectItem key={s} value={s}>{label(s)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        ))}</div>
      )}
    </div>
  );
}

function PassportDialog({ workerId, onClose }) {
  const { data, isLoading, error } = useQuery({ queryKey: ["passport-view", workerId], queryFn: () => api.get(`/provider/me/workers/${workerId}/passport`).then((r) => r.data) });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="provider-passport-dialog">
        <DialogHeader><DialogTitle className="font-display">Zunvel Passport™</DialogTitle></DialogHeader>
        {isLoading ? <Loader /> : error ? <p className="text-sm text-red-600">{errMsg(error)}</p> : (
          <>
            <PassportCard name={data.worker.name} role={data.worker.role} photo={data.worker.photo_url} passportNumber={data.passport_number} readiness={data.readiness} />
            <div className="text-sm font-semibold mt-4">Credentials on file</div>
            <Table><TableHeader><TableRow><TableHead>Credential</TableHead><TableHead>Status</TableHead><TableHead>Expires</TableHead></TableRow></TableHeader>
              <TableBody>{data.credentials.map((c, i) => <TableRow key={i}><TableCell>{c.credential_type}</TableCell><TableCell><StatusPill status={c.status} /></TableCell><TableCell>{fmtDate(c.expiration_date)}</TableCell></TableRow>)}</TableBody></Table>
            <p className="text-xs text-slate-500">Underlying documents and background-check reports are not shared with providers. You see Zunvel's verification result, dates and status only.</p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function ProviderApplicants() {
  const { data, isLoading, refetch } = useQuery({ queryKey: ["provider-applicants"], queryFn: () => api.get("/provider/me/applicants").then((r) => r.data) });
  const [view, setView] = useState(null);
  const update = async (id, status) => { try { await api.patch(`/provider/me/applications/${id}`, { status }); refetch(); } catch (e) { toast.error(errMsg(e)); } };
  if (isLoading) return <Loader />;
  return (
    <div data-testid="provider-applicants">
      <PageHeader title="Applicants" subtitle="Workers who applied with their Zunvel Passport™." />
      {data.length === 0 ? <Empty title="No applicants yet" /> : (
        <div className="cz-card overflow-x-auto"><Table>
          <TableHeader><TableRow><TableHead>Worker</TableHead><TableHead>Job</TableHead><TableHead>Readiness</TableHead><TableHead>Applied</TableHead><TableHead>Status</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>{data.map((a) => (
            <TableRow key={a.id} data-testid={`applicant-row-${a.id}`}>
              <TableCell><div className="font-semibold">{a.worker.name}</div><div className="text-xs text-slate-500">{a.worker.role}</div></TableCell>
              <TableCell className="text-sm">{a.job_title}</TableCell>
              <TableCell><ReadinessBadge status={a.readiness} /></TableCell>
              <TableCell className="text-sm">{fmtDate(a.created_at)}</TableCell>
              <TableCell><Select value={a.status} onValueChange={(v) => update(a.id, v)}><SelectTrigger className="w-36" data-testid={`applicant-status-${a.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{["submitted", "reviewing", "shortlisted", "interview", "rejected", "hired"].map((s) => <SelectItem key={s} value={s}>{label(s)}</SelectItem>)}</SelectContent></Select></TableCell>
              <TableCell><Button size="sm" variant="outline" className="rounded-full" onClick={() => setView(a.worker.id)} data-testid={`view-passport-btn-${a.id}`}><Eye className="h-4 w-4 mr-1" />Passport</Button></TableCell>
            </TableRow>
          ))}</TableBody>
        </Table></div>
      )}
      {view && <PassportDialog workerId={view} onClose={() => setView(null)} />}
    </div>
  );
}

export function ProviderReviews() {
  const { data, isLoading } = useQuery({ queryKey: ["provider-reviews"], queryFn: () => api.get("/provider/me/reviews").then((r) => r.data) });
  if (isLoading) return <Loader />;
  return (
    <div data-testid="provider-reviews">
      <PageHeader title="Reviews" subtitle="Published reviews after Zunvel moderation." />
      {data.length === 0 ? <Empty title="No published reviews yet" /> : <div className="grid gap-3">{data.map((r) => (
        <div key={r.id} className="cz-card p-5"><div className="flex items-center gap-2"><Stars value={r.rating} /><span className="text-sm font-medium">{r.reviewer_display_name}</span><span className="text-xs text-slate-400">{fmtDate(r.created_at)}</span>{r.is_demo && <DemoBadge />}</div><p className="text-sm text-slate-600 mt-2">{r.body}</p></div>
      ))}</div>}
    </div>
  );
}

export function ProviderAnalytics() {
  const { data, isLoading } = useQuery({ queryKey: ["provider-analytics"], queryFn: () => api.get("/provider/me/analytics").then((r) => r.data) });
  if (isLoading) return <Loader />;
  return (
    <div data-testid="provider-analytics">
      <PageHeader title="Analytics" subtitle="Basic activity metrics. Advanced analytics are part of Zunvel Pro (planned)." />
      <div className="grid sm:grid-cols-3 gap-4">
        <StatCard icon={Eye} label="Search appearances" value={data.search_appearances} testid="analytics-appearances" />
        <StatCard icon={FileText} label="Applications" value={data.applications} />
        <StatCard icon={Inbox} label="Care requests" value={Object.values(data.care_requests_by_status).reduce((a, b) => a + b, 0)} />
      </div>
      <div className="cz-card p-6 mt-6"><div className="font-display font-bold mb-3">Care requests by status</div>
        {Object.entries(data.care_requests_by_status).map(([k, v]) => <div key={k} className="flex justify-between py-1.5 text-sm"><StatusPill status={k} /><b>{v}</b></div>)}</div>
    </div>
  );
}

export function ProviderBilling() {
  const plans = useQuery({ queryKey: ["plans"], queryFn: () => api.get("/plans").then((r) => r.data) });
  const org = useQuery({ queryKey: ["provider-me"], queryFn: () => api.get("/provider/me").then((r) => r.data) });
  if (plans.isLoading || org.isLoading) return <Loader />;
  const current = org.data?.subscription?.plan_key || "free";
  return (
    <div data-testid="provider-billing">
      <PageHeader title="Billing" subtitle="Plans and pricing are being finalized. Payments are not active yet — no card is required." />
      <div className="grid md:grid-cols-3 gap-5">{plans.data.map((p) => (
        <div key={p.key} className={`cz-card p-6 flex flex-col ${current === p.key ? "ring-2 ring-teal-600" : ""}`} data-testid={`plan-card-${p.key}`}>
          <div className="flex items-center justify-between"><span className="font-display text-lg font-bold">{p.name}</span>{current === p.key && <StatusPill status="active" />}</div>
          <p className="text-sm text-slate-600 mt-2">{p.description}</p>
          <div className="font-display text-2xl font-bold mt-4">{p.price_monthly != null ? `$${p.price_monthly}/mo` : "Pricing TBA"}</div>
          <ul className="mt-4 space-y-1.5 text-sm text-slate-700 flex-1">{p.features.map((f) => <li key={f} className="flex gap-2"><ShieldCheck className="h-4 w-4 text-teal-600" />{f}</li>)}</ul>
          <Button disabled className="rounded-full mt-5" data-testid={`plan-upgrade-${p.key}`}>{current === p.key ? "Current plan" : "Upgrades coming soon"}</Button>
        </div>
      ))}</div>
    </div>
  );
}
